
import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

@Directive({
  selector: '[floatingTooltip]',
  standalone: true
})
export class FloatingTooltipDirective implements OnChanges, OnDestroy {
  @Input() floatingTooltip = '';

  private tooltipEl?: HTMLDivElement;
  private static lastClientX = -1;
  private static lastClientY = -1;

  constructor(private el: ElementRef<HTMLElement>) {
    const native = this.el.nativeElement;

    native.removeAttribute('title');

    // Do not hide interactive elements from accessibility tools.
    // aria-hidden="true" causes warnings when a button receives focus.
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['floatingTooltip']) return;

    if (!this.floatingTooltip?.trim()) {
      this.removeTooltip();
      return;
    }

    this.tryShowFromCurrentPointer();
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent) {
    FloatingTooltipDirective.lastClientX = event.clientX;
    FloatingTooltipDirective.lastClientY = event.clientY;

    if (!this.floatingTooltip?.trim()) {
      this.removeTooltip();
      return;
    }

    const inside = this.isPointerInside(event.clientX, event.clientY);

    if (inside) {
      if (!this.tooltipEl) this.showTooltipAt(event.clientX, event.clientY);
      else this.positionTooltip(event.clientX, event.clientY);
    } else {
      this.removeTooltip();
    }
  }

  @HostListener('pointerenter', ['$event'])
  onEnter(event: PointerEvent) {
    if (!this.floatingTooltip?.trim()) return;
    this.showTooltipAt(event.clientX, event.clientY);
  }

  @HostListener('pointerleave')
  onLeave() {
    this.removeTooltip();
  }

  @HostListener('click')
  onClick() {
    this.removeTooltip();
  }

  ngOnDestroy(): void {
    this.removeTooltip();
  }

  private tryShowFromCurrentPointer() {
    const x = FloatingTooltipDirective.lastClientX;
    const y = FloatingTooltipDirective.lastClientY;

    if (x < 0 || y < 0) return;
    if (!this.isPointerInside(x, y)) return;

    this.showTooltipAt(x, y);
  }

  private isPointerInside(clientX: number, clientY: number) {
    const rect = this.el.nativeElement.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }

  private showTooltipAt(clientX: number, clientY: number) {
    this.removeTooltip();

    this.tooltipEl = document.createElement('div');
    this.tooltipEl.className = 'floating-tooltip';
    this.tooltipEl.innerHTML = this.floatingTooltip;
    this.tooltipEl.setAttribute('role', 'tooltip');

    document.body.appendChild(this.tooltipEl);
    this.positionTooltip(clientX, clientY);
  }

  private positionTooltip(clientX: number, clientY: number) {
    if (!this.tooltipEl) return;

    const offsetX = 12;
    const offsetY = 10;

    this.tooltipEl.style.left = `${clientX + offsetX}px`;
    this.tooltipEl.style.top = `${clientY - offsetY}px`;
  }

  private removeTooltip() {
    this.tooltipEl?.remove();
    this.tooltipEl = undefined;
  }
}
